import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { t, type Locale, messages } from './messages.ts';

interface I18nValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (section: keyof typeof messages, key: string, replacements?: Record<string, string>) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

const STORAGE_KEY = 'line-proxy:locale';

function detectInitial(): Locale {
  if (typeof window === 'undefined') return 'zh';
  const saved = window.localStorage.getItem(STORAGE_KEY) as Locale | null;
  if (saved === 'zh' || saved === 'en') return saved;
  return navigator.language?.toLowerCase().startsWith('en') ? 'en' : 'zh';
}

export function I18nProvider({ children }: PropsWithChildren) {
  const [locale, setLocaleState] = useState<Locale>(detectInitial);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    window.localStorage.setItem(STORAGE_KEY, l);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale === 'zh' ? 'zh-Hant' : 'en';
  }, [locale]);

  const value = useMemo<I18nValue>(
    () => ({
      locale,
      setLocale,
      t: (section, key, replacements) =>
        // @ts-expect-error narrowed by caller signature; messages typing is strict-deep
        t(section, key, locale, replacements),
    }),
    [locale, setLocale]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const v = useContext(I18nContext);
  if (!v) throw new Error('I18nProvider missing');
  return v;
}
